import type { ReactNode } from "react";
import { SymInfoKlein, SymPersonAus, SymVerboten } from "./lese-symbole";
import { VERWEIS_SYMBOL } from "./lese-verweis-chip";
import { VERWEIS_MODULWORT, type VerweisArt } from "./lese-verweis-art";

/*
 * Hovercard des Lesepfads (Paket E-W1, M-008 D5): die Karte für Wiki-Link, Person, CI und Workflow. Reine Darstellung der
 * Zustände; positioniert und geladen wird sie im Editor (`HornHovercards` in `@horn/editor/kern`), der Zugriff läuft über die
 * bestehenden gesicherten Actions der Seite. Die Karte zeigt nur, was der Lader als lesbar geliefert hat.
 *
 * Sätze (r95, r105): „nicht verfügbar“ (gibt es nicht, gelöscht, kein Recht, kein Lader angebunden) ist je Art EIN fester Satz
 * und nennt weder Titel noch Grund; ein Fehlschlag ist ein anderer, wiederholbarer Satz. Eine Person erscheint ohne E-Mail
 * (der Lader liefert keine). Eigenbau aus den Klassen der Preline-Profil-Popover-Karte: Abweichung AW-052.
 */
export type LeseHovercardArt = "person" | "wiki" | "ci" | "workflow";

export interface LeseHovercardInhalt {
  title: string;
  scope?: string;
  meta?: Array<{ label: string; value: string }>;
  foot?: string;
  /** Fußzeile mit Info-Symbol (Grunddaten-Sicht, W191 A): „Du siehst nur die Grundangaben …“; trennt sich mit Linie ab. */
  hinweis?: string;
  /** Nur gesetzt, wenn der Lader das Recht geprüft hat (CI: `cmdb:read`); es zählen nur relative Adressen. */
  href?: string;
}

export type LeseHovercardZustand =
  | { phase: "laedt" }
  | { phase: "ok"; inhalt: LeseHovercardInhalt }
  | { phase: "unverfuegbar" }
  | { phase: "fehlschlag" };

export const HOVERCARD_SAETZE = {
  wiki: "Diese Seite steht nicht zur Verfügung.",
  person: "Diese Person steht nicht zur Verfügung.",
  ci: "Dieses Element steht nicht zur Verfügung.",
  workflow: "Dieses Element steht nicht zur Verfügung.",
  fehlschlag: "Die Angaben konnten nicht geladen werden. Bitte erneut versuchen.",
} as const;

const KARTE = "w-72 max-w-full bg-popover border border-popover-line text-start rounded-xl shadow-md p-4";

/** Nur Adressen der eigenen Seite (`/…`, nicht `//…`): die Karte verlinkt nie nach außen. */
function relativeAdresse(href: string | undefined): string | null {
  // Rückstrich (auch kodiert) nie: Browser lesen `/\evil.com` als `//evil.com` (wie `sichereAdresse` im Editor-Paket).
  if (!href || /[\u0000- \u007f-\u009f\\]/.test(href) || /%5c/i.test(href)) return null;
  return href.startsWith("/") && !href.startsWith("//") ? href : null;
}

function Initialen({ name }: { name: string }) {
  const teile = name.trim().split(/\s+/).filter(Boolean);
  const buchstaben = (teile.length > 1 ? [teile[0], teile[teile.length - 1]] : teile.slice(0, 1))
    .map((t) => Array.from(t ?? "")[0] ?? "")
    .join("")
    .toUpperCase();
  return (
    <span className="size-10 shrink-0 inline-flex items-center justify-center rounded-full bg-primary-100 text-primary-800 text-sm font-semibold" aria-hidden="true">
      {buchstaben}
    </span>
  );
}

/** Karten-Art zur Verweis-Art (Modulwort und Symbol kommen aus der einen Quelle der Chips). */
const VERWEIS_ART: Record<LeseHovercardArt, VerweisArt> = { ci: "cmdb", workflow: "workflow", person: "person", wiki: "wiki" };

function ArtSymbol({ art }: { art: LeseHovercardArt }): ReactNode {
  const Zeichen = VERWEIS_SYMBOL[VERWEIS_ART[art]];
  return <Zeichen />;
}

/** Oben in der Karte: das Modul mit Symbol (Variante A, wie im Chip). */
function Modulkopf({ art }: { art: LeseHovercardArt }): ReactNode {
  const verweisArt = VERWEIS_ART[art];
  const Zeichen = VERWEIS_SYMBOL[verweisArt];
  return (
    <div className="mb-2">
      <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground-1">
        <Zeichen klein />
        {VERWEIS_MODULWORT[verweisArt]}
      </span>
    </div>
  );
}

export function LeseHovercardKarte({ art, zustand }: { art: LeseHovercardArt; zustand: LeseHovercardZustand }) {
  if (zustand.phase === "laedt") {
    return (
      <>
        {/* Preline: ui-components/ui-components-sub-menus-selection/ui-components-cat-profile-popovers/contact-profile-popover-card */}
        <div className={KARTE} aria-busy="true">
          <p className="text-sm text-muted-foreground-1">Lädt …</p>
        </div>
      </>
    );
  }
  if (zustand.phase === "unverfuegbar") {
    return (
      <>
        {/* Preline: ui-components/ui-components-sub-menus-selection/ui-components-cat-profile-popovers/contact-profile-popover-card */}
        <div className={KARTE}>
          <div className="flex items-start gap-x-3">
            <span className="flex shrink-0 justify-center items-center size-9 bg-layer border border-layer-line text-muted-foreground-1 rounded-lg" aria-hidden="true">
              {art === "person" ? <SymPersonAus /> : <SymVerboten />}
            </span>
            <p className="text-sm text-foreground">{HOVERCARD_SAETZE[art]}</p>
          </div>
        </div>
      </>
    );
  }
  if (zustand.phase === "fehlschlag") {
    return (
      <>
        {/* Preline: ui-components/ui-components-sub-menus-selection/ui-components-cat-profile-popovers/contact-profile-popover-card */}
        <div className={KARTE} role="status">
          <p className="text-sm text-foreground">{HOVERCARD_SAETZE.fehlschlag}</p>
        </div>
      </>
    );
  }
  const { inhalt } = zustand;
  const link = relativeAdresse(inhalt.href);
  return (
    <>
      {/* Preline: ui-components/ui-components-sub-menus-selection/ui-components-cat-profile-popovers/contact-profile-popover-card */}
      <div className={KARTE}>
        <Modulkopf art={art} />
        {/* Ohne Titel (Karte einer Gattung: nur die Art des Elements, W191) steht nur der Modulkopf über dem Hinweis. */}
        {inhalt.title ? (
          <div className="flex items-center gap-x-3">
            {art === "person" ? <Initialen name={inhalt.title} /> : <ArtSymbol art={art} />}
            <div className="min-w-0">
              <div className="text-sm font-semibold text-foreground">{inhalt.title}</div>
              {inhalt.scope ? <div className="text-xs text-muted-foreground-1">{inhalt.scope}</div> : null}
            </div>
          </div>
        ) : null}
        {inhalt.meta && inhalt.meta.length > 0 ? (
          <dl className="mt-3 space-y-1">
            {inhalt.meta.map((m, i) => (
              <div key={i} className="flex gap-x-2 text-xs">
                <dt className="text-muted-foreground-1">{m.label}</dt>
                <dd className="text-foreground">{m.value}</dd>
              </div>
            ))}
          </dl>
        ) : null}
        {inhalt.foot ? <div className="mt-2 text-xs text-muted-foreground-1">{inhalt.foot}</div> : null}
        {inhalt.hinweis ? (
          <div
            className={
              inhalt.title
                ? "mt-3 pt-3 border-t border-line-2 flex items-start gap-x-1.5 text-xs text-muted-foreground-1"
                : "flex items-start gap-x-1.5 text-xs text-muted-foreground-1"
            }
          >
            <span className="mt-px">
              <SymInfoKlein />
            </span>
            <span>{inhalt.hinweis}</span>
          </div>
        ) : null}
        {link ? (
          <div className="mt-3 pt-3 border-t border-line-2">
            <a
              href={link}
              className="inline-flex items-center gap-x-1.5 text-sm text-primary hover:text-primary-hover focus:outline-hidden focus:text-primary-focus"
            >
              {art === "ci" ? "In der CMDB öffnen" : "Öffnen"}
            </a>
          </div>
        ) : null}
      </div>
    </>
  );
}
